import { useCallback } from 'react';
import { AutoSizer } from 'react-virtualized';
import { FixedSizeList } from 'react-window';
import {
  useFlexLayout,
  useResizeColumns,
  useRowState,
  useSortBy,
  useTable,
} from 'react-table';
import Text from '../../../atoms/Text';
import Image from '../../../atoms/Image';
import shevron_down from '../../../assets/images/shevron_down.svg';
import shevron_up from '../../../assets/images/shevron_up.svg';

import './styles.scss';

interface IBodyTableProps {
  data: any[];
  columns: any[];
  renderCell(cell: any, row: any): React.ReactNode;
  rowClass?: string;
}

const scrollbarWidth = 4;

const BodyTable = ({
  data,
  columns,
  renderCell,
  rowClass,
}: IBodyTableProps) => {
  const tableInstance = useTable(
    { columns: columns, data },
    useSortBy,
    useFlexLayout,
    useResizeColumns,
    useRowState
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    totalColumnsWidth,
  } = tableInstance;

  const RenderRow = useCallback(
    ({ index, style }) => {
      const row: any = rows[index];

      prepareRow(row);
      return (
        <div
          {...row.getRowProps()}
          style={{
            ...style,
            display: 'flex',
          }}
          className={`tr ${rowClass || ''}`}
        >
          {row.cells.map((cell: any) => (
            <div className='td' key={index} {...cell.getCellProps()}>
              {renderCell(cell, row.original)}
            </div>
          ))}
        </div>
      );
    },
    [prepareRow, rows]
  );

  const HeaderGroup = ({ headerGroup }: any) => (
    <div className='tr thr' {...headerGroup.getHeaderGroupProps()}>
      {headerGroup.headers.map((column: any) => (
        <div
          className='th'
          key={JSON.stringify(column)}
          {...column.getHeaderProps(column.getSortByToggleProps())}
        >
          <Text className={column.attach_right ? 'attach_right' : ''} size={12}>
            {column.render('Header')}
          </Text>
          {!column.disableSortBy && column.isSorted ? (
            column.isSortedDesc ? (
              <Image src={shevron_down} size='s' />
            ) : (
              <Image src={shevron_up} size='s' />
            )
          ) : null}
        </div>
      ))}
    </div>
  );

  return (
    <div className='table' {...getTableProps()} style={{maxWidth: '100%'}}>
      <div className='thead'>
        {headerGroups.map((headerGroup, index) => (
          <HeaderGroup key={index} headerGroup={headerGroup} />
        ))}
      </div>
      <div className='tbody' {...getTableBodyProps()}>
        <AutoSizer>
          {({ height }) => (
            <FixedSizeList
              height={height}
              itemCount={rows.length}
              itemSize={28}
              width={totalColumnsWidth + scrollbarWidth}
              className='modal_body_table'
            >
              {RenderRow}
            </FixedSizeList>
          )}
        </AutoSizer>
      </div>
    </div>
  );
};

export default BodyTable;
